import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  FiMapPin,
  FiChevronDown,
  FiCalendar,
  FiBriefcase,
  FiImage,
  FiFolder,
  FiMail,
  FiMessageSquare,
  FiUser,
  FiLogOut,
  FiSettings,
  FiKey,
  FiLayers,
} from 'react-icons/fi';
import { Link, useNavigate, useLocation } from '@tanstack/react-router';
import { useAuth } from '../../hooks/useAuth';
import { useDiscoveryStore } from '../../store/useDiscoveryStore';
import { LocationModal } from '../common/LocationModal';
import { LocationSearchInput } from '../common/LocationSearchInput';
import { WorkspaceModal } from '../common/WorkspaceModal';
import { Avatar } from '../ui/Avatar';

const logo = '/Musicade-Logo.png';

export const Header: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const signInRedirect =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : location.pathname;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
  const [navDropdownOpen, setNavDropdownOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false); // mobile only
  const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1133 : false,
  );
  const { selectedCity, setSelectedCity } = useDiscoveryStore();

  const headerRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const mobileUserDropdownRef = useRef<HTMLDivElement>(null);
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const navDropdownRef = useRef<HTMLDivElement>(null);

  // Dynamically set --header-height so pages never use magic numbers
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const updateHeight = () => {
      document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
    };
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
      if (
        mobileUserDropdownRef.current &&
        !mobileUserDropdownRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Track viewport for responsive nav switch
  useEffect(() => {
    const handleResize = () => setIsNarrowViewport(window.innerWidth < 1133);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleProjectsClick = (e?: React.SyntheticEvent) => {
    if (e) e.preventDefault();
    if (!isAuthenticated) {
      const searchStr = typeof window !== 'undefined' ? window.location.search : '';
      const currentUrl = location.pathname + searchStr;
      const separator = currentUrl.includes('?') ? '&' : '?';
      const redirectUrl = `${currentUrl}${separator}workspaceModal=true`;
      navigate({ to: '/sign-in', search: { redirect: redirectUrl } });
    } else {
      setWorkspaceModalOpen(true);
    }
  };

  useEffect(() => {
    const isModalRequested =
      (location.search as Record<string, string>)?.workspaceModal === 'true' ||
      (typeof window !== 'undefined' && window.location.search.includes('workspaceModal=true'));

    if (isAuthenticated && isModalRequested) {
      setWorkspaceModalOpen(true);
    }
  }, [isAuthenticated, location.search]);

  const handleCloseWorkspaceModal = () => {
    setWorkspaceModalOpen(false);
    if (typeof window !== 'undefined' && window.location.search.includes('workspaceModal=true')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('workspaceModal');
      const newSearch = url.search ? url.search : '';
      window.history.replaceState({}, '', url.pathname + newSearch + url.hash);
    }
  };

  const dropdownItems: Array<{
    icon: React.ReactNode;
    label: string;
    href?: '/gigs/create' | '/media/create' | '/services/create' | '/mail' | '/events/create';
    action?: () => void;
  }> = [
    { icon: <FiBriefcase size={20} />, label: 'Gigs', href: '/gigs/create' },
    { icon: <FiImage size={20} />, label: 'Media', href: '/media/create' },
    { icon: <FiFolder size={20} />, label: 'Services', href: '/services/create' },
    { icon: <FiMail size={20} />, label: 'Mail', href: '/mail' },
    { icon: <FiCalendar size={20} />, label: 'Event', href: '/events/create' },
    {
      icon: <FiLayers size={20} />,
      label: 'Projects',
      action: () => {
        setDropdownOpen(false);
        handleProjectsClick();
      },
    },
  ];

  const navItems = [
    { to: '/projects' as const, label: 'Projects', icon: <FiLayers size={15} /> },
    { to: '/gigs' as const, label: 'Gigs', icon: <FiBriefcase size={15} /> },
    { to: '/media' as const, label: 'Media', icon: <FiImage size={15} /> },
    { to: '/services' as const, label: 'Services', icon: <FiFolder size={15} /> },
    { to: '/events' as const, label: 'Events', icon: <FiCalendar size={15} /> },
  ];

  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-50 bg-black/40"
      style={{
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      {/* Desktop Header content (hidden on mobile) */}
      <div className="hidden md:flex items-center justify-between h-22 px-8 py-4">
        {/* Left: Logo */}
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3 cursor-pointer group">
            {/* Mobile logo (shown on narrow viewports: md but < 1133px) */}
            <img
              src="/Musicade-Logo.png"
              alt="Musicade Logo"
              className="w-8 h-8 object-contain min-[1133px]:hidden"
            />
            {/* Full logo (hidden on narrow viewports) */}
            <img
              src={logo}
              alt="Musicade Logo"
              className="w-10 h-10 object-contain hidden min-[1133px]:block"
            />
            <div className="flex items-center gap-2">
              <span className="text-white font-['Bebas_Neue'] text-[22px] leading-none tracking-normal uppercase">
                Musicade
              </span>
              <span className="rounded-full bg-[#2B1A14] px-3 py-1 text-[12px] font-semibold leading-none text-[#F7963B]">
                Beta
              </span>
            </div>
          </Link>
        </div>

        {/* {isHomePage && (
            <div className="hidden lg:flex items-center flex-1 max-w-125.5" style={{ height: 48 }}>
              <div className="relative flex items-center bg-[#1a1a1a] border border-white/5 hover:border-white/10 transition-colors rounded-full px-5 gap-3 w-full h-full shadow-inner">
                <FiSearch size={18} className="text-zinc-500 shrink-0" />
                <input
                  type="text"
                  placeholder="Discover artists, venues, events..."
                  className="bg-transparent border-none text-white text-[14px] font-['Inter'] font-normal placeholder:text-zinc-600 focus:ring-0 focus:outline-none w-full"
                />
                <div className="h-4 w-px bg-white/10 mx-1" />
                <FiSliders
                  size={18}
                  className="text-zinc-500 shrink-0 cursor-pointer hover:text-white transition-colors"
                />
              </div>
            </div>
          )} */}

        {/* Center: Navigation */}
        <div className="hidden md:flex items-center">
          {isNarrowViewport ? (
            <div className="relative" ref={navDropdownRef}>
              <button
                className={`flex items-center gap-2 rounded-full px-4 transition-all border cursor-pointer ${
                  navDropdownOpen
                    ? 'bg-zinc-800 border-white/20'
                    : 'bg-[#1a1a1a] border-white/5 hover:border-white/10 hover:bg-white/5'
                }`}
                style={{ height: 40 }}
                onClick={() => setNavDropdownOpen((p) => !p)}
              >
                <span className="text-zinc-300 text-[13px] font-['Inter'] font-medium">
                  {navItems.find((i) => location.pathname.startsWith(i.to))?.label || 'Menu'}
                </span>
                <FiChevronDown
                  size={13}
                  className={`text-zinc-500 shrink-0 transition-transform duration-200 ${
                    navDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {navDropdownOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-[#1a1a1a]/95 backdrop-blur-2xl border border-white/10 rounded-4xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-50 p-2"
                  style={{ animation: 'fadeInScale 0.2s ease-out' }}
                >
                  <div className="flex flex-col gap-1">
                    {navItems.map((item) => {
                      const isActive = location.pathname.startsWith(item.to);
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 ${
                            isActive
                              ? 'bg-white/10 text-white'
                              : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                          }`}
                          onClick={(e) => {
                            setNavDropdownOpen(false);
                            if (item.to === '/projects') {
                              handleProjectsClick(e);
                            }
                          }}
                        >
                          <span className={isActive ? 'text-[#F7963B]' : 'text-zinc-500'}>
                            {item.icon}
                          </span>
                          <span className="text-[14px] font-['Inter'] font-medium">
                            {item.label}
                          </span>
                          {isActive && (
                            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#F7963B]" />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = location.pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`group relative flex items-center gap-2.5 rounded-full px-4 py-2 text-[13px] font-['Inter'] font-medium transition-all duration-200 ${
                      isActive
                        ? 'text-white bg-white/10'
                        : 'text-zinc-400 hover:text-white hover:bg-white/[0.04]'
                    }`}
                    onClick={(e) => {
                      if (item.to === '/projects') {
                        handleProjectsClick(e);
                      }
                    }}
                  >
                    <span
                      className={`transition-colors duration-200 ${
                        isActive ? 'text-[#F7963B]' : 'text-zinc-500 group-hover:text-zinc-300'
                      }`}
                    >
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                    {isActive && (
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-4 h-[2px] bg-[#F7963B] rounded-full" />
                    )}
                  </Link>
                );
              })}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-5">
          {/* Portal backdrop — rendered into document.body, completely outside header DOM */}
          {isLocationMenuOpen &&
            createPortal(
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 39,
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onPointerDown={() => setIsLocationMenuOpen(false)}
                onTouchStart={() => setIsLocationMenuOpen(false)}
                onClick={() => setIsLocationMenuOpen(false)}
                aria-hidden="true"
              />,
              document.body,
            )}

          {/* Location dropdown */}
          <div className="relative" ref={locationDropdownRef}>
            <button
              className={`flex items-center gap-2 rounded-full px-4 transition-all border cursor-pointer group ${
                isLocationMenuOpen
                  ? 'bg-zinc-800 border-white/20'
                  : 'bg-[#1a1a1a] border-white/5 hover:border-white/10 hover:bg-white/5'
              }`}
              style={{ height: 40 }}
              onClick={() => {
                setIsLocationMenuOpen((p) => !p);
              }}
            >
              <FiMapPin size={15} className="text-[#3B82F6] shrink-0" />
              <span className="text-zinc-300 text-[13px] font-['Inter'] font-medium whitespace-nowrap max-w-30 truncate">
                {selectedCity || 'All Locations'}
              </span>
              <FiChevronDown
                size={13}
                className={`text-zinc-500 shrink-0 transition-transform duration-200 ${
                  isLocationMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isLocationMenuOpen && (
              <div
                className="absolute right-0 mt-3 w-90 max-w-[90vw] bg-[#111111]/98 backdrop-blur-3xl border border-white/10 rounded-[32px] shadow-[0_20px_80px_rgba(0,0,0,0.7)] z-50 overflow-hidden flex flex-col"
                style={{ animation: 'fadeInScale 0.18s ease-out' }}
              >
                {/* Header row */}
                <div className="px-6 pt-6 pb-4 border-b border-white/5">
                  <p className="text-zinc-500 text-[11px] font-bold uppercase tracking-[0.15em] mb-4">
                    Discovery Location
                  </p>

                  <LocationSearchInput
                    onLocationSelected={(loc) => {
                      if (loc) {
                        setSelectedCity(loc.city);
                        setIsLocationMenuOpen(false);
                      }
                    }}
                    placeholder="Type to search city..."
                    lax={true}
                  />
                </div>

                {/* Footer info */}
                <div className="px-6 py-4 bg-white/[0.02] border-t border-white/5 text-center">
                  <p className="text-zinc-600 text-[10px] font-semibold uppercase tracking-widest">
                    Powered by Google Maps
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={dropdownRef}>
            <div
              className={`hidden sm:flex items-center gap-2 rounded-full px-4 cursor-pointer group transition-all duration-300 ${dropdownOpen ? 'bg-zinc-100 ring-4 ring-white/10' : 'bg-white hover:bg-zinc-100'}`}
              style={{ height: 40 }}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <FiCalendar size={16} className="text-black" />
              <span className="text-black text-[14px] font-['Inter'] font-medium group-hover:opacity-80 transition-opacity">
                Create
              </span>
              <FiChevronDown
                size={16}
                className={`text-black/60 ml-1 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}
              />
            </div>

            {dropdownOpen && (
              <>
                <div
                  className="fixed bg-black/60 backdrop-blur-[20px] transition-all duration-300 pointer-events-auto z-40"
                  style={{
                    top: '88px',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    WebkitBackdropFilter: 'blur(20px)',
                  }}
                  onClick={() => setDropdownOpen(false)}
                />

                <div
                  className="absolute left-1/2 -translate-x-1/2 mt-3 w-70 max-w-[90vw] bg-[#1a1a1a]/95 backdrop-blur-2xl border border-white/10 rounded-4xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-50 p-2"
                  style={{
                    animation: 'fadeInScale 0.2s ease-out',
                  }}
                >
                  <div className="flex flex-col gap-1">
                    {dropdownItems.map((item, index) => {
                      if (item.action) {
                        return (
                          <button
                            key={index}
                            className="flex items-center justify-between px-5 py-4 rounded-2xl transition-all duration-200 group hover:bg-white/5 cursor-pointer w-full"
                            onClick={item.action}
                          >
                            <div className="flex items-center gap-4">
                              <div className="transition-colors text-zinc-500 group-hover:text-white">
                                {item.icon}
                              </div>
                              <span className="text-[17px] font-['Inter'] font-medium transition-colors text-white">
                                {item.label}
                              </span>
                            </div>
                          </button>
                        );
                      }
                      return (
                        <Link
                          key={index}
                          to={item.href!}
                          className="flex items-center justify-between px-5 py-4 rounded-2xl transition-all duration-200 group hover:bg-white/5 cursor-pointer"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="transition-colors text-zinc-500 group-hover:text-white">
                              {item.icon}
                            </div>
                            <span className="text-[17px] font-['Inter'] font-medium transition-colors text-white">
                              {item.label}
                            </span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          {isAuthenticated ? (
            <div className="flex items-center gap-4">
              <div className="relative" ref={userDropdownRef}>
                <div
                  className={`flex items-center gap-3 rounded-full pl-2 pr-4 cursor-pointer transition-all border border-white/5 ${userDropdownOpen ? 'bg-zinc-800 border-white/20' : 'bg-[#1a1a1a] hover:border-white/10 hover:bg-white/5'}`}
                  style={{ height: 40 }}
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                >
                  <Avatar
                    src={user?.profPics}
                    alt="User"
                    name={user?.fullName || user?.username}
                    size="sm"
                    className="shrink-0 border border-white/10"
                  />
                  <span className="text-white text-[14px] font-['Inter'] font-medium min-w-15">
                    {user?.username || user?.fullName || (
                      <div className="h-4 w-16 bg-white/10 rounded animate-[pulse-subtle_2s_infinite]" />
                    )}
                  </span>
                  <FiChevronDown
                    size={14}
                    className={`text-zinc-500 transition-transform duration-300 ${userDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </div>

                {userDropdownOpen && (
                  <div className="absolute right-0 mt-3 w-56 bg-[#1a1a1a]/95 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl z-50 p-1">
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      <FiUser size={16} className="text-zinc-500" />
                      Profile
                    </Link>
                    <Link
                      to="/events"
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      <FiCalendar size={16} className="text-zinc-500" />
                      My Events
                    </Link>
                    <Link
                      to="/profile/my-gigs"
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      <FiBriefcase size={16} className="text-zinc-500" />
                      My Gigs
                    </Link>
                    <Link
                      to="/messages"
                      search={{ conversationUuid: undefined }}
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      <FiMessageSquare size={16} className="text-zinc-500" />
                      Inbox
                    </Link>
                    <Link
                      to="/profile/change-password"
                      className="flex items-center gap-3 w-full px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px] text-left"
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      <FiKey size={16} className="text-zinc-500" />
                      Change Password
                    </Link>
                    <Link
                      to="/profile/settings"
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      <FiSettings size={16} className="text-zinc-500" />
                      Settings
                    </Link>
                    <div className="h-px bg-white/5 my-1" />
                    <button
                      onClick={() => {
                        logout();
                        setUserDropdownOpen(false);
                        navigate({ to: '/sign-in' });
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-red-500 hover:bg-red-500/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                    >
                      <FiLogOut size={16} />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <Link
              to="/sign-in"
              search={{ redirect: signInRedirect }}
              className="text-zinc-300 hover:text-white text-[14px] font-['Inter'] font-medium cursor-pointer transition-colors"
            >
              Sign in
            </Link>
          )}
        </div>
      </div>

      {/* Mobile Header (hidden on desktop) */}
      <div className="md:hidden flex flex-col px-4 pt-3 pb-3 gap-3">
        <div className="flex items-center justify-between h-12">
          <Link to="/" className="relative flex items-center gap-2">
            <img src="/Musicade-Logo.png" alt="Musicade Logo" className="w-8 h-8 object-contain" />
            <span className="rounded-full bg-[#2B1A14] px-2 py-0.5 text-[9px] font-semibold leading-none text-[#F7963B]">
              Beta
            </span>
          </Link>

          <div className="flex items-center gap-2.5">
            {isHomePage && (
              <button
                onClick={() => setLocationModalOpen(true)}
                className="flex items-center gap-2 rounded-full px-3.5 py-2 bg-[#1a1a1a] border border-white/10 shadow-lg active:scale-95 transition-all cursor-pointer hover:border-white/20"
              >
                <FiMapPin size={13} className="text-blue-400 shrink-0" />
                <span className="text-white text-[12px] font-medium font-['Inter'] max-w-24 truncate">
                  {selectedCity || 'All'}
                </span>
                <FiChevronDown size={11} className="text-zinc-500 shrink-0" />
              </button>
            )}

            {isAuthenticated ? (
              <div className="relative" ref={mobileUserDropdownRef}>
                <div
                  className="cursor-pointer shadow-lg active:scale-95 transition-transform"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  <Avatar
                    src={user?.profPics}
                    alt="User"
                    name={user?.fullName || user?.username}
                    size="md"
                    className="border border-white/10"
                  />
                </div>

                {mobileMenuOpen && (
                  <div className="absolute right-0 mt-3 w-48 bg-[#1a1a1a]/95 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl z-50 p-1 animate-[fadeInScale_0.2s_ease-out] origin-top-right">
                    <div className="flex flex-col gap-0.5 px-4 pt-3 pb-2 border-b border-white/5 mb-1">
                      <span className="text-white text-[13px] font-bold truncate">
                        {user?.fullName || user?.username || 'User'}
                      </span>
                      <span className="text-zinc-500 text-[11px] font-medium truncate">
                        @{user?.username || 'user'}
                      </span>
                    </div>
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiUser size={16} className="text-zinc-500" />
                      Profile
                    </Link>
                    <Link
                      to="/projects"
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={(e) => {
                        setMobileMenuOpen(false);
                        handleProjectsClick(e);
                      }}
                    >
                      <FiLayers size={16} className="text-zinc-500" />
                      Projects
                    </Link>
                    <Link
                      to="/events"
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiCalendar size={16} className="text-zinc-500" />
                      My Events
                    </Link>
                    <Link
                      to="/mail"
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiMail size={16} className="text-zinc-500" />
                      Mail
                    </Link>
                    <Link
                      to="/profile/my-gigs"
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiBriefcase size={16} className="text-zinc-500" />
                      My Gigs
                    </Link>
                    <Link
                      to="/messages"
                      search={{ conversationUuid: undefined }}
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiMessageSquare size={16} className="text-zinc-500" />
                      Inbox
                    </Link>
                    <Link
                      to="/profile/change-password"
                      className="flex items-center gap-3 w-full px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px] text-left"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiKey size={16} className="text-zinc-500" />
                      Change Password
                    </Link>
                    <Link
                      to="/profile/settings"
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiSettings size={16} className="text-zinc-500" />
                      Settings
                    </Link>
                    <div className="h-px bg-white/5 my-1" />
                    <button
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                        navigate({ to: '/sign-in' });
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-red-500 hover:bg-red-500/5 transition-colors rounded-xl font-['Inter'] text-[14px]"
                    >
                      <FiLogOut size={16} />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/sign-in"
                search={{ redirect: signInRedirect }}
                className="h-10 px-5 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white text-[13px] font-semibold backdrop-blur-md active:scale-95 transition-all"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>

        {/* {isHomePage && (
          <div className="relative flex items-center bg-[#1a1a1a] border border-white/5 rounded-full px-4 h-11 shadow-inner">
            <FiSearch size={18} className="text-zinc-500 shrink-0" />
            <input
              type="text"
              placeholder="Search Artists, Venues..."
              className="ml-2 bg-transparent border-none text-white text-[14px] font-['Inter'] font-normal placeholder:text-zinc-600 focus:ring-0 focus:outline-none w-full"
            />
          </div>
        )} */}
      </div>

      {locationModalOpen && <LocationModal onClose={() => setLocationModalOpen(false)} />}
      {workspaceModalOpen && <WorkspaceModal onClose={handleCloseWorkspaceModal} />}

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95) translateY(-10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </header>
  );
};
